import { get, uniq, uniqBy } from 'lodash';
import { action, computed, makeObservable, observable, runInAction, toJS } from 'mobx';

import {
  GOOGLE_SHEET_LINK_PATTERN,
  SERVER_NO_DRIVERS_ERROR,
  SERVER_REGIONS_REQUIRED_ERROR,
  IMPORT_BULK_URL,
  IMPORT_FILE_PATTERN,
  IMPORT_OPTIONS_PAGE_SIZE,
  IMPORT_ROW_STATUS,
  IMPORT_SCHEDULE_ERROR_KEY,
  IMPORT_SERVER_STATUS,
  IMPORT_SOURCE,
  IMPORT_UPLOAD_ERRORS,
  IMPORT_VALIDATE_URL,
} from 'constants/announcementImport';
import { toastMessage } from 'constants/toastMessage';
import {
  getErrorKeys,
  getImportRowStatus,
  GROUP_ID_FIELDS,
  hasDriverIds,
  hasSelectedGroups,
  NOT_FOUND_FIELDS,
  pairOriginNames,
  normalizeImportRow,
  ROW_ERROR_KEY,
  toImportPayload,
  toServerErrors,
} from 'selectors/announcementImport';

/**
 * Preview rows of a bulk announcement import.
 *
 * Rows are kept by id in an observable map, with the order in a separate `rowIds` array that is
 * only replaced on load. Editing a cell mutates one field of one row, so only the observers that
 * read that field react; the list itself, and every other row, does not re-render.
 *
 * The import API validates rows first; each row's remaining API errors live in `serverErrorsById`
 * ({ field: [messages] }). Editing a field clears its API error, and client validation takes over.
 *
 * Importing posts the ready rows to the bulk create API, which returns the rows that didn't fully
 * succeed, by id with their errors: created but not scheduled (a `schedule` error: WARNING) or not
 * created (other errors). Created rows leave the review; rows not created stay as errors, on top, and
 * the rows that weren't ready when submitted stay as ignored (`ignoredIds`) until edited. `createdById`
 * keeps the created ones (with any warning messages) for the announcements list; it outlives the
 * review, until the next validate.
 */

/**
 * Options for the Pool and Crew cells come from the API: all driver pools once per review
 * (`loadPools`), and driver crews per region, only when a cell needs them (`loadCrews`). Crews are
 * cached by region in `crewsByRegion`: an array once loaded, null while its request is in flight.
 */
const toOption = (item, nameField) => ({ id: item.id, name: item[nameField] || item.name || String(item.id), region: item.region });

// A crew's drivers, when the API lists them; undefined otherwise, which leaves the drivers check to the server.
const toCrewOption = (crew) => ({ ...toOption(crew, 'name'), driverCount: Array.isArray(crew.drivers) ? crew.drivers.length : undefined });

// A pool's id without the region suffix the pools API adds to it; see toPoolOptions.
const toPoolId = (pool) => String(pool.id).replace(/_[^_]*$/, '');

// GET /driver-pools returns a record per pool and region, its id suffixed with the region code
// (`<poolId>_PHL`, `<poolId>_null` without one), while the validate API gives the bare pool id. Pools
// are kept one per bare id, the first record's name, and with no region: they aren't region-specific.
const toPoolOptions = (pools) =>
  uniqBy(
    pools.map((pool) => ({ id: toPoolId(pool), name: pool.tag || pool.name || String(pool.id) })),
    'id',
  );

const STATUS_IDLE = 'idle';
const STATUS_LOADING = 'loading';
const STATUS_LOADED = 'loaded';

class DriverAnnouncementImportStore {
  rowIds = [];
  rowsById = new Map();
  serverErrorsById = new Map();
  source = null;
  sourceLabel = '';
  loading = false;
  importing = false;
  // Rows that weren't ready at the last submit and haven't been edited since; rebuilt on every submit.
  ignoredIds = new Set();
  // A submit went through in this review, and whether the last one created every row it sent.
  hasImported = false;
  isFinished = false;
  // Announcements created by the bulk imports since the last validate: id -> { status, messages }. Kept
  // across the announcement pages, until a new validate or the announcements section is left.
  createdById = new Map();
  // The announcements list shows only those created with warnings (its "Show attention records").
  showingAttention = false;
  // The import API's validation error, with the source so the dialog shows it on the matching tab.
  error = null;
  poolOptions = [];
  // The pool records as GET /driver-pools returns them, one per pool and region (with `region` and
  // `no_of_drivers`), kept for checking a row's driver count later; poolOptions folds them per pool.
  allRegionsPools = [];
  poolsStatus = STATUS_IDLE;
  crewsByRegion = new Map();
  // Not observable: each row's status as a MobX computed, created on first read (see getRowStatus).
  rowStatuses = new Map();
  // Not observable (fixed once rows load): per row and pool/crew field, the ids the API matched and
  // the sheet names paired with them ({ ids, names: { id: name } }).
  originNamesById = new Map();

  constructor(api, regionStore) {
    this.api = api;
    this.regionStore = regionStore;
    const store = this;
    // One stable object the row cells read options from; its getters are observable, so a cell only
    // re-renders for the lists it actually reads.
    this.lookups = {
      get regionCodes() {
        return store.regionCodes;
      },
      get poolOptions() {
        return store.poolOptions;
      },
      get poolsById() {
        return store.poolsById;
      },
      get poolDriversById() {
        return store.poolDriversById;
      },
      get crewsByRegion() {
        return store.crewsByRegion;
      },
      get crewsById() {
        return store.crewsById;
      },
      loadCrews: (regions) => store.loadCrews(regions),
      rowStatus: (id) => store.getRowStatus(id),
      originNames: (rowId, field) => (store.originNamesById.get(rowId) || {})[field] || { ids: [], names: {} },
    };
    makeObservable(this, {
      rowIds: observable.ref,
      rowsById: observable,
      serverErrorsById: observable,
      source: observable,
      sourceLabel: observable,
      loading: observable,
      importing: observable,
      ignoredIds: observable,
      createdById: observable,
      showingAttention: observable,
      setShowingAttention: action,
      hasImported: observable,
      isFinished: observable,
      ignoredCount: computed,
      warningCount: computed,
      createdCount: computed,
      warningIds: computed,
      error: observable.ref,
      poolOptions: observable.ref,
      allRegionsPools: observable.ref,
      poolsStatus: observable,
      crewsByRegion: observable,
      regionCodes: computed,
      poolsById: computed,
      poolDriversById: computed,
      crewsById: computed,
      statusCounts: computed,
      readyCount: computed,
      fixCount: computed,
      attentionCount: computed,
      setRows: action,
      updateRow: action.bound,
      reset: action,
      clearError: action,
      loadRows: action,
      rejectUpload: action,
      importRows: action,
      applyBulkResults: action,
      clearCreated: action,
      clearImport: action,
      loadPools: action,
      loadCrews: action,
    });
  }

  get statusCounts() {
    const counts = Object.fromEntries(Object.values(IMPORT_ROW_STATUS).map((status) => [status, 0]));
    this.rowIds.forEach((id) => {
      counts[this.getRowStatus(id)] += 1;
    });
    return counts;
  }

  get readyCount() {
    return this.statusCounts[IMPORT_ROW_STATUS.READY];
  }

  get fixCount() {
    return this.statusCounts[IMPORT_ROW_STATUS.FIX];
  }

  get ignoredCount() {
    return this.statusCounts[IMPORT_ROW_STATUS.IGNORED];
  }

  // Rows that won't be imported as they are: in error, or ignored by the last submit.
  get attentionCount() {
    return this.fixCount + this.ignoredCount;
  }

  // Announcements created in this review (the created rows have left it), and those with warnings.
  get createdCount() {
    return this.createdById.size;
  }

  get warningCount() {
    return this.warningIds.length;
  }

  // The announcements created with warnings, which the announcements page points out until cleared.
  get warningIds() {
    return [...this.createdById.entries()].filter(([, created]) => created.status === IMPORT_ROW_STATUS.WARNING).map(([id]) => id);
  }

  get regionCodes() {
    const regions = this.regionStore ? this.regionStore.regions : [];
    return regions && regions.length ? regions.map((region) => region.value) : null;
  }

  loadRegions() {
    if (this.regionStore) {
      this.regionStore.init();
    }
  }

  get poolsById() {
    return new Map(this.poolOptions.map((pool) => [pool.id, pool]));
  }

  // Each pool's drivers per region code, from allRegionsPools: pool id -> Map(REGION -> no_of_drivers).
  // Records without a region add nothing, since no row region reaches them.
  get poolDriversById() {
    const byId = new Map();
    this.allRegionsPools.forEach((pool) => {
      const counts = byId.get(toPoolId(pool)) || new Map();
      const region = String(pool.region || '')
        .trim()
        .toUpperCase();
      if (region) {
        counts.set(region, (counts.get(region) || 0) + (Number(pool.no_of_drivers) || 0));
      }
      byId.set(toPoolId(pool), counts);
    });
    return byId;
  }

  get crewsById() {
    const crews = [...this.crewsByRegion.values()].filter(Array.isArray).flat();
    return new Map(crews.map((crew) => [crew.id, crew]));
  }

  // All driver pools, once per review: the API can't filter them by region, and there aren't many.
  loadPools() {
    if (this.poolsStatus !== STATUS_IDLE) {
      return Promise.resolve();
    }
    this.poolsStatus = STATUS_LOADING;
    return this.api.get('/driver-pools', { page: 0, size: IMPORT_OPTIONS_PAGE_SIZE }).then((response) => {
      runInAction(() => {
        if (!response.ok) {
          // Back to idle so the next review tries again.
          this.poolsStatus = STATUS_IDLE;
          return;
        }
        this.allRegionsPools = get(response, 'data.items', []);
        this.poolOptions = toPoolOptions(this.allRegionsPools);
        this.poolsStatus = STATUS_LOADED;
      });
    });
  }

  // Driver crews for the given regions, fetching only regions not loaded or loading yet, in one
  // request. A failed request forgets those regions so opening the dropdown again retries them.
  loadCrews(regions) {
    const missing = regions.filter((region) => !this.crewsByRegion.has(region));
    if (!missing.length) {
      return Promise.resolve();
    }
    missing.forEach((region) => this.crewsByRegion.set(region, null));
    // An array, which axios sends as regions[]=PHL&regions[]=SFO, the format the API expects.
    return this.api.get('/driver-crews', { regions: missing, page: 0, size: IMPORT_OPTIONS_PAGE_SIZE }).then((response) => {
      runInAction(() => {
        if (!response.ok) {
          missing.forEach((region) => this.crewsByRegion.delete(region));
          return;
        }
        const crews = get(response, 'data.items', []).map(toCrewOption);
        missing.forEach((region) => {
          this.crewsByRegion.set(
            region,
            crews.filter((crew) => String(crew.region).toLowerCase() === region.toLowerCase()),
          );
        });
      });
    });
  }

  // A row's status: the one value the status chip, the counts and the import all read, so they always
  // agree. Each row's is a MobX computed, created on first read and cached until something it depends on
  // changes (its fields, its API errors, being ignored, or the loaded regions, pools and crews), so an
  // edit re-validates only the edited row.
  getRowStatus(id) {
    if (!this.rowStatuses.has(id)) {
      this.rowStatuses.set(
        id,
        computed(() => this.validateRow(id)),
      );
    }
    return this.rowStatuses.get(id).get();
  }

  validateRow(id) {
    const row = this.rowsById.get(id);
    if (!row) {
      return null;
    }
    if (this.ignoredIds.has(id)) {
      return IMPORT_ROW_STATUS.IGNORED;
    }
    return getImportRowStatus(row, {
      serverErrors: this.serverErrorsById.get(id),
      regionCodes: this.regionCodes,
      poolDriversById: this.poolDriversById,
      crewsById: this.crewsById,
    });
  }

  // `results` are the import API's rows: { status, data, errors }.
  setRows(results, source, sourceLabel) {
    this.rowsById.clear();
    this.serverErrorsById.clear();
    this.originNamesById = new Map();
    this.rowStatuses = new Map();
    this.clearImport();
    this.rowIds = results.map((result) => this.putRow(result));
    this.source = source;
    this.sourceLabel = sourceLabel;
  }

  // Puts one API row ({ status, data, errors }) in the review, replacing any with its id: normalised for
  // editing, its API errors as its starting state, and its sheet pool/crew names paired with the ids.
  // Called from setRows (an action). Returns the row id.
  putRow(result) {
    const row = normalizeImportRow(result.data, { regionCodes: this.regionCodes });
    this.rowsById.set(row.id, row);
    this.serverErrorsById.set(row.id, toServerErrors(result));
    const serverErrors = this.serverErrorsById.get(row.id);
    const pair = (field, originField) => {
      const messages = getErrorKeys(field).flatMap((key) => (serverErrors && serverErrors[key]) || []);
      const { names, unmatched } = pairOriginNames(row[originField], row[field], messages);
      // Names the API couldn't match become values of their own, shown in red until removed.
      this.rowsById.get(row.id)[NOT_FOUND_FIELDS[field]] = unmatched;
      return { ids: [...row[field]], names };
    };
    this.originNamesById.set(row.id, {
      driver_pool_ids: pair('driver_pool_ids', 'origin_driver_pools'),
      driver_crew_ids: pair('driver_crew_ids', 'origin_driver_crews'),
    });
    this.dropAnsweredDriverErrors(row.id);
    this.dropRedundantErrorsWithDriverIds(row.id);
    return row.id;
  }

  // A pool or crew selected (an unmatched name included) on a row without driver IDs answers the API's
  // "drivers required" on Driver IDs, so its errors there are dropped. With driver IDs, they're about
  // the IDs themselves (e.g. not found), so they stay. Called from setRows and updateRow (actions).
  dropAnsweredDriverErrors(id) {
    const row = this.rowsById.get(id);
    const serverErrors = this.serverErrorsById.get(id);
    if (row && serverErrors && hasSelectedGroups(row) && !hasDriverIds(row)) {
      getErrorKeys('drivers').forEach((key) => delete serverErrors[key]);
    }
  }

  // Issue: The API checks the pools and crews for drivers, and the regions they need, even on a row with driver
  // IDs, where only the IDs are used; its "no drivers" errors on Pool and Crew and its "regions required" error
  // on Region don't apply there, so they're dropped. Their other errors still count.
  dropRedundantErrorsWithDriverIds(id) {
    const row = this.rowsById.get(id);
    const serverErrors = this.serverErrorsById.get(id);
    if (!row || !serverErrors || !hasDriverIds(row)) {
      return;
    }
    const redundant = [
      [GROUP_ID_FIELDS, SERVER_NO_DRIVERS_ERROR],
      [['driver_regions'], SERVER_REGIONS_REQUIRED_ERROR],
    ];
    redundant.forEach(([fields, pattern]) =>
      fields
        .flatMap(getErrorKeys)
        .filter((key) => serverErrors[key])
        .forEach((key) => {
          const remaining = serverErrors[key].filter((message) => !pattern.test(message));
          if (remaining.length) {
            serverErrors[key] = remaining;
          } else {
            delete serverErrors[key];
          }
        }),
    );
  }

  updateRow(id, field, value) {
    const row = this.rowsById.get(id);
    if (!row) {
      return;
    }
    row[field] = value;
    // Edited since the last submit, so the row has its own status again.
    this.ignoredIds.delete(id);
    // Pools and crews only exist within regions, so removing every region clears them too.
    const clearsGroups = field === 'driver_regions' && !(value && value.length);
    if (clearsGroups) {
      GROUP_ID_FIELDS.forEach((groupField) => {
        row[groupField] = [];
        row[NOT_FOUND_FIELDS[groupField]] = [];
      });
    }
    // The API's verdict on this field (and any row-level one) no longer applies once it's edited.
    const serverErrors = this.serverErrorsById.get(id);
    if (serverErrors) {
      (clearsGroups ? [field, ...GROUP_ID_FIELDS] : [field]).flatMap(getErrorKeys).forEach((key) => delete serverErrors[key]);
      delete serverErrors[ROW_ERROR_KEY];
      if (GROUP_ID_FIELDS.includes(field)) {
        this.dropAnsweredDriverErrors(id);
      }
      if (field === 'drivers') {
        this.dropRedundantErrorsWithDriverIds(id);
      }
    }
  }

  reset() {
    this.rowsById.clear();
    this.serverErrorsById.clear();
    this.originNamesById = new Map();
    this.rowStatuses = new Map();
    this.rowIds = [];
    this.source = null;
    this.sourceLabel = '';
    this.loading = false;
    this.importing = false;
    this.error = null;
    // Not createdById: the announcements list tags those after the review closes.
    this.clearImport();
    this.poolOptions = [];
    this.allRegionsPools = [];
    this.poolsStatus = STATUS_IDLE;
    this.crewsByRegion.clear();
  }

  clearError() {
    this.error = null;
  }

  clearCreated() {
    this.createdById.clear();
    this.showingAttention = false;
  }

  setShowingAttention(showing) {
    this.showingAttention = showing;
  }

  clearImport() {
    this.ignoredIds.clear();
    this.hasImported = false;
    this.isFinished = false;
  }

  // Creates the ready rows (only those) with the bulk create API. Resolves to its response; `importing`
  // is true meanwhile. On success the results are applied (see applyBulkResults); on failure nothing
  // changes. With no ready row, nothing is sent.
  async importRows() {
    const ids = this.rowIds.filter((id) => this.getRowStatus(id) === IMPORT_ROW_STATUS.READY);
    if (!ids.length) {
      return { ok: true, status: 200, data: [] };
    }
    this.importing = true;
    try {
      const rows = ids.map((id) => toImportPayload(toJS(this.rowsById.get(id)), { regionCodes: this.regionCodes }));
      const response = await this.api.post(IMPORT_BULK_URL, rows);
      if (response.ok) {
        this.applyBulkResults(ids, response.data);
      }
      return response;
    } finally {
      runInAction(() => {
        this.importing = false;
      });
    }
  }

  // The bulk create API returns only the rows that didn't fully succeed, as [{ id, errors }] (an empty
  // array, or nothing, when all did). Of the sent rows, one with a `schedule` error was created with a
  // warning, one with other errors wasn't created, and the rest were. Created rows leave the review. The
  // rows not created stay as sent with those errors, on top; then the rows that weren't sent (not ready)
  // as ignored, until edited. The review is finished when nothing came back in error.
  applyBulkResults(sentIds, data) {
    // [{ id, errors }]: a `schedule` error means created with a warning; any other errors, not created.
    const returned = new Map((Array.isArray(data) ? data : []).filter((result) => result && result.id).map((result) => [result.id, result]));
    const notSentIds = this.rowIds.filter((id) => !sentIds.includes(id));
    const errorIds = [];
    sentIds.forEach((id) => {
      const result = returned.get(id);
      const errors = (result && result.errors) || {};
      if (result && !errors[IMPORT_SCHEDULE_ERROR_KEY]) {
        // No data comes back: the row stays as sent, with the errors, like a validated row in error.
        this.serverErrorsById.set(id, toServerErrors({ status: IMPORT_SERVER_STATUS.ERROR, errors }));
        this.dropAnsweredDriverErrors(id);
        this.dropRedundantErrorsWithDriverIds(id);
        errorIds.push(id);
        return;
      }
      const status = result ? IMPORT_ROW_STATUS.WARNING : IMPORT_ROW_STATUS.DONE;
      this.createdById.set(id, { status, messages: uniq(Object.values(errors).flat()) });
      this.rowsById.delete(id);
      this.serverErrorsById.delete(id);
      this.originNamesById.delete(id);
      this.rowStatuses.delete(id);
    });
    this.ignoredIds.replace(notSentIds);
    this.rowIds = [...errorIds, ...notSentIds];
    this.hasImported = true;
    this.isFinished = !errorIds.length;
  }

  // Sends the Google Sheet link to the validate API, after checking it looks like one.
  fetchSheetRows(sheetLink) {
    if (!GOOGLE_SHEET_LINK_PATTERN.test(sheetLink)) {
      return this.rejectUpload(IMPORT_SOURCE.SHEET, IMPORT_UPLOAD_ERRORS.INVALID_SHEET_LINK);
    }
    return this.loadRows(() => this.postForValidation('url', sheetLink), IMPORT_SOURCE.SHEET, 'Google Sheet');
  }

  // Sends the .csv or .xlsx file to the validate API, after checking its type.
  fetchFileRows(file) {
    if (!IMPORT_FILE_PATTERN.test(file.name)) {
      return this.rejectUpload(IMPORT_SOURCE.FILE, IMPORT_UPLOAD_ERRORS.UNSUPPORTED_FILE);
    }
    return this.loadRows(() => this.postForValidation('file', file), IMPORT_SOURCE.FILE, file.name);
  }

  // POST /driver-announcements/bulk/validate as form-data with a `url` or `file` field
  postForValidation(field, value) {
    const form = new FormData();
    form.append(field, value);
    return this.api.post(IMPORT_VALIDATE_URL, form);
  }

  rejectUpload(source, message) {
    this.error = { source, message };
    return Promise.resolve(false);
  }

  async loadRows(request, source, sourceLabel) {
    this.loading = true;
    this.error = null;
    // A new validate starts a new import: the last one's created announcements are no longer new.
    this.createdById.clear();
    try {
      const response = await request();
      const loaded = response.ok && Array.isArray(response.data);
      runInAction(() => {
        if (loaded) {
          this.setRows(response.data, source, sourceLabel);
        } else {
          this.error = { source, message: get(response, 'data.message') || toastMessage.SOMETHING_WENT_WRONG };
        }
      });
      return loaded;
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }
}

export default DriverAnnouncementImportStore;
